import { createSelector } from "reselect"
import { get, chain, map, includes, compact } from "lodash"
import { METRICS } from "helpers/metrics/constants"
import { getBaseMetricTypeOptions } from "selectors/assets/metricTypes"
import { METRIC_TYPES } from "helpers/assets/constants"
import { getDefaultCurrencyId, getIsOpPro } from "modules/account-root/selectors"
import { getCurrency } from "modules/portfolio-root/selectors/portfolios"
import { makeCurrencyFormatter } from "helpers/formatters/numbers"

const ALLOWED_METRIC_TYPES = [METRIC_TYPES.NET_CONTRIBUTION, METRIC_TYPES.CURRENCY_EXPOSURE, METRIC_TYPES.CURRENCY_PERFORMANCE]

const EMPTY_OBJECT = {}

const SUPPORTED_METRICS = {
  [METRICS.EXPOSURE]: { label: "Exposure", disabled: false },
  [METRICS.PERFORMANCE]: { label: "Performance", disabled: false },
  [METRICS.RISK]: { label: "Risk", disabled: true },
}

export const getDefaultMetricUnits = state => get(state, "modules.defaultMetricUnits", EMPTY_OBJECT)
const getMetric = (state, props) => get(props, "metric", null)

export const getMetricUnit = createSelector(
  [getDefaultMetricUnits, getMetric],
  (units, metric) => get(units, metric, METRIC_TYPES.NET_CONTRIBUTION),
)

export const getMetricUnitOptions = createSelector(
  [getMetric, getMetricUnit, getBaseMetricTypeOptions],
  (metric, metricUnit, baseOptions) => chain(baseOptions)
    .get(metric)
    .pickBy(({ id }) => includes(ALLOWED_METRIC_TYPES, id))
    .map((option) => ({
      ...option,
      isDefault: option.id === metricUnit,
    }))
    .value(),
)

const getMakeMetricOptionsFn = createSelector(
  [getDefaultCurrencyId, getCurrency],
  (accountCurrency, portfolioCurrency) => (metricId) => {
    const currencyFormatter = makeCurrencyFormatter(portfolioCurrency || accountCurrency)
    return compact([
      { id: METRIC_TYPES.NET_CONTRIBUTION, label: "Net Contribution", unit: includes([METRICS.EXPOSURE, METRICS.BETA], metricId) ? "#" : "%" },
      metricId === METRICS.EXPOSURE && { id: METRIC_TYPES.CURRENCY_EXPOSURE, label: "Currency", unit: portfolioCurrency || accountCurrency, formatter: currencyFormatter },
      metricId === METRICS.PERFORMANCE && { id: METRIC_TYPES.CURRENCY_PERFORMANCE, label: "Currency", unit: portfolioCurrency || accountCurrency, formatter: currencyFormatter },
    ])
  },
)

export const getSupportedMetrics = createSelector(
  [getDefaultMetricUnits, getMakeMetricOptionsFn, getIsOpPro],
  (defaultMetricUnits, makeMetricOptionsFn, isOpPro) => chain(SUPPORTED_METRICS)
    .mapValues((metric, metricId) => ({
      ...metric,
      ...(isOpPro && metricId === METRICS.RISK && { disabled: true, upsell: true, tooltip: "Only available with enterprise plan" }),
      options: map(makeMetricOptionsFn(metricId), option => ({
        ...option,
        isDefault: option.id === (defaultMetricUnits[metricId] || METRIC_TYPES.NET_CONTRIBUTION),
      })),
    }))
    .value(),
)
